import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { Badge, type BadgeTone } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ClockIcon, MailIcon, ShieldIcon, TrophyIcon } from '../components/icons';
import { displayNameOf, formatDateTime } from '../lib/utils';

function StatCard({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 pt-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-soft-fg">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
          <p className="mt-1 truncate text-sm font-semibold text-fg">{value}</p>
          {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusRow({ label, tone, value }: { label: string; tone: BadgeTone; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted">{label}</span>
      <Badge tone={tone}>{value}</Badge>
    </div>
  );
}

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const { t, locale } = useTranslation();

  if (!user) {
    // ProtectedRoute guarantees a user; keep this defensive.
    return null;
  }

  const name = displayNameOf(user);
  const statusTone: BadgeTone = user.status === 'active' ? 'success' : 'warning';

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">
            {t('dashboard.title', { name })}
          </h1>
          <p className="mt-1 text-sm text-muted">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <Link to="/admin">
              <Button variant="outline">
                <ShieldIcon className="h-4 w-4" />
                {t('dashboard.adminConsole')}
              </Button>
            </Link>
          )}
          <Link to="/profile">
            <Button variant="ghost">{t('dashboard.viewProfile')}</Button>
          </Link>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t('dashboard.stat.displayName')} value={name} icon={TrophyIcon} />
        <StatCard label={t('dashboard.stat.email')} value={user.email} icon={MailIcon} />
        <StatCard
          label={t('dashboard.stat.role')}
          value={t(isAdmin ? 'role.admin' : 'role.user')}
          hint={t(isAdmin ? 'dashboard.roleHint.admin' : 'dashboard.roleHint.user')}
          icon={ShieldIcon}
        />
        <StatCard
          label={t('dashboard.stat.memberSince')}
          value={formatDateTime(user.createdAt, locale)}
          hint={t('dashboard.stat.lastLogin', { date: formatDateTime(user.lastLoginAt, locale) })}
          icon={ClockIcon}
        />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('dashboard.contests.title')}</CardTitle>
            <CardDescription>{t('dashboard.contests.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-strong bg-surface-muted px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface text-brand shadow-sm">
                <TrophyIcon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 font-semibold text-fg">
                {t('dashboard.contests.empty.title')}
              </h3>
              <p className="mt-1 max-w-sm text-sm text-muted">
                {t('dashboard.contests.empty.body')}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.account.title')}</CardTitle>
            <CardDescription>{t('dashboard.account.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <StatusRow
              label={t('dashboard.account.status')}
              tone={statusTone}
              value={t(`status.${user.status}` as const)}
            />
            <StatusRow
              label={t('dashboard.account.emailVerified')}
              tone={user.emailVerified ? 'success' : 'neutral'}
              value={user.emailVerified ? t('status.verified') : t('status.notVerified')}
            />
            <StatusRow
              label={t('dashboard.account.role')}
              tone={isAdmin ? 'brand' : 'neutral'}
              value={t(isAdmin ? 'role.admin' : 'role.user')}
            />
            <div className="border-t border-border pt-3">
              <p className="text-xs text-muted">{t('dashboard.account.id')}</p>
              <p className="mt-1 break-all font-mono text-xs text-fg">{user.id}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
